/**
 * Local notifications for native builds.
 *
 * Schedules up to 3 recurring local notifications to keep learners engaged:
 *   1. Daily practice reminder — 7pm local, every day.
 *   2. Word of the Day — 9am local, every day.
 *   3. Sound of the Week — Monday 10am local.
 *
 * Implementation details:
 *   - Uses @capacitor/local-notifications which fires entirely on-device
 *     (no server push needed). Great default for engagement.
 *   - For true push notifications (server-initiated), add Firebase Cloud
 *     Messaging on Android + APNs on iOS — documented in MOBILE_SETUP.md.
 *   - Scheduling is idempotent: we clear previous schedules with the same
 *     ids before re-scheduling, so calling this on every app launch is safe.
 *   - No-op on web.
 */
import { isNative } from './native';

const REMINDERS = [
  { id: 1001, title: 'Ready for a quick chat?', body: 'A 3-minute Spanish session keeps your streak alive.', at: { hour: 19, minute: 0 } },
  { id: 1002, title: 'New Word of the Day', body: 'Tap to learn today\'s word.', at: { hour: 9,  minute: 0 } },
  { id: 1003, title: 'This week\'s sound',    body: 'Practice the pronunciation of the week.',              at: { hour: 10, minute: 0, weekday: 2 } }, // 2 = Monday in Capacitor
];

export async function scheduleEngagementNotifications() {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');

    // Request permission on first call.
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== 'granted') return;
    }

    // Clear previously-scheduled LingoFlow notifications (idempotent).
    const pending = await LocalNotifications.getPending();
    const toCancel = (pending.notifications || []).filter((n) =>
      REMINDERS.some((r) => r.id === n.id),
    );
    if (toCancel.length) {
      await LocalNotifications.cancel({ notifications: toCancel.map((n) => ({ id: n.id })) });
    }

    // Schedule fresh.
    await LocalNotifications.schedule({
      notifications: REMINDERS.map((r) => ({
        id: r.id,
        title: r.title,
        body: r.body,
        schedule: { on: r.at, repeats: true, allowWhileIdle: true },
      })),
    });
  } catch {
    /* Permission denied or plugin missing — silently degrade. */
  }
}
